import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Subject, takeUntil, of, forkJoin } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import {
  PurchaseOrder,
  PurchaseOrderStatus,
  PurchaseOrderPriority,
  PurchaseOrderApproval,
  PurchaseOrderItem,
  PurchaseOrderStatusHistory,
  ApprovalStatus
} from '../../../models/PurchaseOrder';
import {
  PurchaseOrderApprovalTracking,
  PurchaseOrderApprovalTrackingStep
} from '../../../models/PurchaseOrderApprovalTracking';
import {
  PurchaseOrderQuotation,
  PurchaseOrderQuotationHeader,
  PurchaseOrderQuotationLine
} from '../../../models/PurchaseOrderQuotation';
import { PurchaseOrderService } from '../../../services/purchase-order.service';
import { PurchaseRequestSimpleViewResponse } from '../../../services/purchase-order.service';
import { PurchaseOrderApprovalService } from '../../../services/purchase-order-approval.service';
import { PurchaseOrderItemService } from '../../../services/purchase-order-item.service';
import { PurchaseOrderQuotationsService } from '../../../services/purchase-order-quotations.service';
import {
  StockMovementsService,
  EntryAwaitingReceiptPayload
} from '../../../services/stock-movements.service';
import { WarehousesService } from '../../../services/warehouses.service';
import { Warehouse } from '../../../models/Warehouse';
import { ErrorService } from '../../../shared/services/error.service';
import { AuthService } from '../../../services/auth.service';
import { StaffService } from '../../../services/staff.service';
import { StaffMember } from '../../../models/StaffMember';
import { TableColumn } from '../../../shared/components/table/table.component';

/** Modelo local do formulário da modal de «Indicar compra → estoque». */
interface StockEntryLineFormModel {
  purchaseOrderItemId: number;
  expectedItem: {
    name: string;
    code: string;
    supplierCode: string;
    unitOfMeasure: string;
    estimatedUnitCost: number;
    category: string;
  };
  quantity: number;
  note: string;
}

interface StockEntryFormModel {
  notes: string;
  warehouseId: number | null;
  lines: StockEntryLineFormModel[];
}
@Component({
  selector: 'app-purchase-order-details',
  templateUrl: './purchase-order-details.component.html',
  styleUrls: ['./purchase-order-details.component.css']
})
export class PurchaseOrderDetailsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private baseRoute = '/purchase-orders';
  private modalDecisionStep: PurchaseOrderApprovalTrackingStep | null = null;
  private modalDecisionKind: 'approve' | 'reject' | null = null;

  // ===== PROPRIEDADES =====
  purchaseOrder: PurchaseOrder | null = null;
  items: PurchaseOrderItem[] = [];
  approvals: PurchaseOrderApproval[] = [];
  statusHistory: PurchaseOrderStatusHistory[] = [];
  isLoading = false;
  itemsLoading = false;
  isConfirmingPurchase = false;
  error: string | null = null;
  purchaseOrderId: number | null = null;

  // ===== MODAL «Indicar compra» → entrada aguardando recebimento =====
  showStockEntryModal = false;
  warehouses: Warehouse[] = [];
  isLoadingWarehouses = false;
  warehousesLoadError = false;
  isLoadingWinnerProposal = false;
  hasWinnerProposal = false;
  private winnerProposalLines = new Map<number, PurchaseOrderQuotationLine>();
  stockEntryForm: StockEntryFormModel = { notes: '', warehouseId: null, lines: [] };
  /** Nome do gestor designado (carregado via staff ou vindo da API); nunca exibir só o ID. */
  managerDesignatedDisplay: string | null = null;

  /** Abas: visão geral vs rastreamento GET `/purchase-orders/{id}/approval-tracking`. */
  detailsTab: 'overview' | 'approval-tracking' = 'overview';
  approvalTracking: PurchaseOrderApprovalTracking | null = null;
  approvalTrackingLoading = false;
  approvalTrackingError: string | null = null;
  trackingDecisionBusyId: number | null = null;
  trackingComments: Record<number, string> = {};
  trackingModalOpen = false;
  trackingModalComment = '';

  // ===== CONFIGURAÇÃO DA TABELA DE ITENS (alinhado ao DTO /complete da API) =====
  /** Colunas da tabela de itens no fluxo Compras (pedido completo). */
  private readonly purchaseOrderItemColumns: TableColumn[] = [
    { titulo: 'Produto', label: 'productName' },
    {
      titulo: 'Qtd solicitada',
      label: 'quantityRequested',
      formatter: PurchaseOrderDetailsComponent.fmtQty
    },
    {
      titulo: 'Qtd aprovada',
      label: 'quantityApproved',
      formatter: PurchaseOrderDetailsComponent.fmtQty
    },
    { titulo: 'Status', label: 'status' },
    {
      titulo: 'Total',
      label: 'displayLineTotal',
      formatter: PurchaseOrderDetailsComponent.fmtBrl
    }
  ];

  private readonly purchaseRequestSimpleItemColumns = [
    { titulo: 'Produto', label: 'productName' },
    { titulo: 'Qtd Solicitada', label: 'quantityRequested' },
    { titulo: 'Unidade', label: 'unit' },
    { titulo: 'Justificativa', label: 'justification' }
  ];

  get itemColumns() {
    return this.isSolicitacaoComprasFlow
      ? this.purchaseRequestSimpleItemColumns
      : this.purchaseOrderItemColumns;
  }

  // ===== ENUMS PARA TEMPLATE =====
  PurchaseOrderStatus = PurchaseOrderStatus;
  PurchaseOrderPriority = PurchaseOrderPriority;
  ApprovalStatus = ApprovalStatus;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private purchaseOrderService: PurchaseOrderService,
    private approvalService: PurchaseOrderApprovalService,
    private itemService: PurchaseOrderItemService,
    private errorService: ErrorService,
    private staffService: StaffService,
    private quotationsService: PurchaseOrderQuotationsService,
    private warehousesService: WarehousesService,
    private stockMovementsService: StockMovementsService,
    private authService: AuthService
  ) {}

  get isSolicitacaoComprasFlow(): boolean {
    return this.baseRoute === '/purchase-requests';
  }

  /** Rótulo do documento no cabeçalho (Pedido vs Solicitação). */
  get detailsDocumentLabel(): string {
    return this.isSolicitacaoComprasFlow ? 'Solicitação de Compra' : 'Pedido de Compra';
  }

  /** Aprovações ordenadas por ordem no fluxo e data (visão geral). */
  get sortedApprovals(): PurchaseOrderApproval[] {
    const list = [...(this.approvals || [])];
    list.sort((a, b) => {
      const ra = a as unknown as Record<string, unknown>;
      const rb = b as unknown as Record<string, unknown>;
      const oa = Number(ra['approvalOrder'] ?? ra['ApprovalOrder'] ?? a.approvalOrder ?? 0);
      const ob = Number(rb['approvalOrder'] ?? rb['ApprovalOrder'] ?? b.approvalOrder ?? 0);
      if (oa !== ob) return oa - ob;
      const da = a.approvalDate ? new Date(a.approvalDate as Date).getTime() : 0;
      const db = b.approvalDate ? new Date(b.approvalDate as Date).getTime() : 0;
      return da - db;
    });
    return list;
  }

  ngOnInit(): void {
    this.baseRoute = this.router.url.startsWith('/purchase-requests')
      ? '/purchase-requests'
      : '/purchase-orders';
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.purchaseOrderId = +params['id'];
        const tabQ = this.route.snapshot.queryParamMap.get('tab');
        const openApproval =
          !this.isSolicitacaoComprasFlow &&
          (tabQ === 'approval' || tabQ === 'approval-tracking');
        this.detailsTab = openApproval ? 'approval-tracking' : 'overview';
        this.approvalTracking = null;
        this.approvalTrackingError = null;
        this.loadPurchaseOrder();
        if (!this.isSolicitacaoComprasFlow) {
          this.loadStatusHistory();
          if (openApproval) {
            this.loadApprovalTracking();
          }
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== CARREGAMENTO DE DADOS =====

  /**
   * Carrega dados completos do pedido
   */
  loadPurchaseOrder(): void {
    if (!this.purchaseOrderId) return;

    this.isLoading = true;
    this.error = null;
    
    if (this.isSolicitacaoComprasFlow) {
      this.purchaseOrderService.getPurchaseRequestSimpleView(this.purchaseOrderId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (simpleView: PurchaseRequestSimpleViewResponse) => {
            this.purchaseOrder = this.mapSimpleViewToPurchaseOrder(simpleView);
            this.managerDesignatedDisplay = null;
            this.items = ((simpleView.items || []) as unknown) as PurchaseOrderItem[];
            this.approvals = [];
            this.statusHistory = [];
            this.isLoading = false;
          },
          error: (error: unknown) => {
            this.error = 'Erro ao carregar solicitação de compra. Tente novamente.';
            this.isLoading = false;
            console.error('Erro ao carregar solicitação:', error);
          }
        });
      return;
    }

    this.itemsLoading = true;
    this.purchaseOrderService
      .getPurchaseOrderComplete(this.purchaseOrderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (order: PurchaseOrder) => {
          this.purchaseOrder = order;
          this.resolveManagerDesignatedDisplay(order);
          const embedded = order.items ?? [];
          if (embedded.length > 0) {
            this.items = embedded.map((row) =>
              this.mapItemRowForDisplay(row as PurchaseOrderItem)
            ) as PurchaseOrderItem[];
            this.itemsLoading = false;
          } else {
            this.items = [];
            this.loadItems();
          }
          const embAppr = order.approvals ?? [];
          if (embAppr.length > 0) {
            this.approvals = embAppr;
          } else {
            this.loadApprovals();
          }
          this.isLoading = false;
        },
        error: (error: unknown) => {
          this.error =
            this.errorService.formatHttpErrorForDisplay(error) ||
            'Erro ao carregar pedido de compra. Tente novamente.';
          this.isLoading = false;
          this.itemsLoading = false;
          console.error('Erro ao carregar pedido:', error);
        }
      });
  }

  /**
   * Preenche o nome do gestor designado para exibição (sem mostrar ID ao usuário).
   */
  private resolveManagerDesignatedDisplay(order: PurchaseOrder): void {
    this.managerDesignatedDisplay = null;
    const sid = order.managerApproverStaffId;
    if (sid == null || !Number.isFinite(Number(sid)) || Number(sid) <= 0) {
      this.managerDesignatedDisplay = '';
      return;
    }
    const fromOrder = order.managerApproverName?.trim();
    if (fromOrder) {
      this.managerDesignatedDisplay = fromOrder;
      return;
    }
    this.managerDesignatedDisplay = null;
    this.staffService
      .getStaffById(Number(sid), { suppressErrorToast: true })
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null))
      )
      .subscribe((staff) => {
        if (!staff) {
          this.managerDesignatedDisplay = 'Nome indisponível';
          return;
        }
        const s = staff as StaffMember & { fullName?: string };
        const n = (s.FullName || s.fullName || '').trim();
        this.managerDesignatedDisplay = n || 'Nome indisponível';
      });
  }

  /**
   * Carrega itens do pedido
   */
  loadItems(): void {
    if (!this.purchaseOrderId) return;

    this.itemsLoading = true;
    
    this.itemService.getItemsByPurchaseOrder(this.purchaseOrderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.items = items.map((row) => this.mapItemRowForDisplay(row));
          this.itemsLoading = false;
        },
        error: (error) => {
          console.error('Erro ao carregar itens:', error);
          this.itemsLoading = false;
        }
      });
  }

  /**
   * Carrega aprovações do pedido
   */
  loadApprovals(): void {
    if (!this.purchaseOrderId) return;
    
    this.approvalService.getApprovalsByPurchaseOrder(this.purchaseOrderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (approvals) => {
          this.approvals = approvals;
        },
        error: (error) => {
          console.error('Erro ao carregar aprovações:', error);
        }
      });
  }

  /**
   * Carrega histórico de status
   */
  loadStatusHistory(): void {
    if (!this.purchaseOrderId) return;
    
    // TODO: Implementar método getStatusHistory no serviço
    this.statusHistory = [];
  }

  /**
   * Recarrega todos os dados
   */
  retryLoad(): void {
    this.loadPurchaseOrder();
    if (!this.isSolicitacaoComprasFlow) {
      this.loadStatusHistory();
    }
  }

  // ===== NAVEGAÇÃO =====

  /**
   * Volta para lista
   */
  goBack(): void {
    this.router.navigate([this.baseRoute]);
  }

  /**
   * Navega para edição
   */
  editOrder(): void {
    this.router.navigate([this.baseRoute, this.purchaseOrderId, 'edit']);
  }

  /** Cotações: até 3 propostas por processo (purchase-order-quotation-headers). */
  goToQuotations(): void {
    if (!this.purchaseOrderId || this.isSolicitacaoComprasFlow) return;
    this.router.navigate(['/purchase-orders', this.purchaseOrderId, 'quotations']);
  }

  selectDetailsTab(tab: 'overview' | 'approval-tracking'): void {
    this.detailsTab = tab;
    if (tab === 'approval-tracking' && !this.isSolicitacaoComprasFlow && this.purchaseOrderId) {
      this.loadApprovalTracking();
    }
  }

  loadApprovalTracking(): void {
    if (!this.purchaseOrderId || this.isSolicitacaoComprasFlow) return;
    this.approvalTrackingLoading = true;
    this.approvalTrackingError = null;
    this.purchaseOrderService
      .getApprovalTracking(this.purchaseOrderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (t) => {
          this.approvalTracking = t;
          this.approvalTrackingLoading = false;
        },
        error: (err: unknown) => {
          this.approvalTrackingLoading = false;
          this.approvalTracking = null;
          this.approvalTrackingError =
            this.errorService.formatHttpErrorForDisplay(err) ||
            'Não foi possível carregar o rastreamento de aprovações.';
        }
      });
  }

  /**
   * Exibe ações de aprovar/rejeitar quando a etapa está pendente e a API não bloqueou (`canEdit === false`).
   * Quem pode de fato aprovar é validado no POST; o JWT pode não bater com `staffId` da etapa (claims diferentes).
   */
  canActOnApprovalStep(step: PurchaseOrderApprovalTrackingStep): boolean {
    if (step.canEdit === false) return false;
    const action = String(step.action || '').trim();
    if (action !== 'Pendente') return false;
    return true;
  }

  openTrackingDecisionModal(
    step: PurchaseOrderApprovalTrackingStep,
    kind: 'approve' | 'reject'
  ): void {
    if (step.id == null) return;
    this.modalDecisionStep = step;
    this.modalDecisionKind = kind;
    this.trackingModalComment = (this.trackingComments[step.id] || '').trim();
    this.trackingModalOpen = true;
  }

  closeTrackingDecisionModal(): void {
    if (this.trackingDecisionBusyId != null) return;
    this.trackingModalOpen = false;
    this.modalDecisionStep = null;
    this.modalDecisionKind = null;
    this.trackingModalComment = '';
  }

  confirmTrackingDecisionFromModal(): void {
    if (!this.modalDecisionStep || !this.modalDecisionKind) return;
    this.submitTrackingDecision(this.modalDecisionStep, this.modalDecisionKind, this.trackingModalComment);
  }

  get trackingModalTitle(): string {
    return this.modalDecisionKind === 'reject' ? 'Confirmar rejeição' : 'Confirmar aprovação';
  }

  get trackingModalConfirmLabel(): string {
    return this.modalDecisionKind === 'reject' ? 'Confirmar rejeição' : 'Confirmar aprovação';
  }

  get trackingModalCommentLabel(): string {
    return this.modalDecisionKind === 'reject'
      ? 'Motivo da rejeição'
      : 'Comentário (opcional)';
  }

  get trackingModalCommentPlaceholder(): string {
    return this.modalDecisionKind === 'reject'
      ? 'Ex.: Divergência de valores com a cotação aprovada.'
      : 'Ex.: Aprovado conforme faixa e critérios definidos.';
  }

  /**
   * Aprovação por faixa: apenas duas ações são permitidas — `approve` e `reject`.
   * Aprovar envia `{ comments }`; rejeitar envia `{ rejectionReason }` (obrigatório).
   */
  submitTrackingDecision(
    step: PurchaseOrderApprovalTrackingStep,
    kind: 'approve' | 'reject',
    decisionText?: string
  ): void {
    const id = step.id;
    if (id == null) return;
    const text = (decisionText ?? this.trackingComments[id] ?? '').trim();
    if (kind === 'reject' && !text) {
      this.errorService.showErrorMessage('Informe o motivo da rejeição.');
      return;
    }
    this.trackingDecisionBusyId = id;
    const req$ =
      kind === 'approve'
        ? this.approvalService.approveApproval(id, text || undefined)
        : this.approvalService.rejectApproval(id, text);
    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.trackingDecisionBusyId = null;
        this.trackingComments[id] = '';
        this.closeTrackingDecisionModal();
        this.errorService.showSuccessMessage(
          kind === 'approve' ? 'Aprovação registrada.' : 'Rejeição registrada.'
        );
        this.loadApprovalTracking();
        this.loadPurchaseOrder();
      },
      error: (err: unknown) => {
        this.trackingDecisionBusyId = null;
        this.errorService.handleHttpError(err);
      }
    });
  }

  formatApprovalTrackingMoney(v: number | undefined): string {
    if (v == null || !Number.isFinite(Number(v))) return '—';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v));
  }

  formatApprovalTrackingWhen(s: string | undefined): string {
    if (!s) return '—';
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleString('pt-BR');
  }

  trackingStepBadgeClass(step: PurchaseOrderApprovalTrackingStep): string {
    const c = String(step.statusColor || '').toLowerCase();
    if (c === 'warning') return 'bg-warning text-dark';
    if (c === 'success') return 'bg-success';
    if (c === 'danger') return 'bg-danger';
    return 'bg-secondary';
  }

  /** Abre a tela de decisão do gestor (aprovar / reprovar / solicitar ajustes). */
  goToManagerDecision(): void {
    if (!this.purchaseOrderId || !this.isSolicitacaoComprasFlow) return;
    this.router.navigate([this.baseRoute, this.purchaseOrderId, 'approve']);
  }

  /**
   * Duplica pedido
   */
  duplicateOrder(): void {
    // TODO: Implementar duplicação de pedido
    this.errorService.showInfoMessage('Funcionalidade em desenvolvimento');
  }

  // ===== AÇÕES =====

  /**
   * Envia pedido para aprovação
   */
  sendToApproval(): void {
    if (!this.purchaseOrder) return;

    const staffId = 1; // TODO: Obter do usuário logado
    
    this.purchaseOrderService.sendToApproval(this.purchaseOrder.id, {
      staffId,
      createdBy: this.resolveCreatedByQueryForSendToApproval()
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.errorService.showInfoMessage('Pedido enviado para aprovação com sucesso');
          this.retryLoad();
        },
        error: (error) => {
          this.errorService.handleHttpError(error);
        }
      });
  }

  /**
   * Cancela pedido
   */
  cancelOrder(): void {
    if (!this.purchaseOrder) return;

    if (!confirm(`Deseja realmente cancelar o pedido ${this.purchaseOrder.orderNumber}?`)) {
      return;
    }

    const reason = prompt('Motivo do cancelamento:');
    if (!reason) return;

    const staffId = 1; // TODO: Obter do usuário logado
    
    this.purchaseOrderService.cancelPurchaseOrder(this.purchaseOrder.id, { 
      staffId, 
      cancellationReason: reason 
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.errorService.showInfoMessage('Pedido cancelado com sucesso');
          this.retryLoad();
        },
        error: (error) => {
          this.errorService.handleHttpError(error);
        }
      });
  }

  // ===== MÉTODOS DE VERIFICAÇÃO =====

  /**
   * Verifica se pode editar
   */
  canEdit(): boolean {
    if (!this.purchaseOrder) return false;
    return this.purchaseOrder.status === PurchaseOrderStatus.SOLICITADO || 
           this.purchaseOrder.status === PurchaseOrderStatus.EM_APROVACAO;
  }

  /**
   * Verifica se pode aprovar (fluxo pedido de compra completo).
   */
  canApprove(): boolean {
    if (!this.purchaseOrder) return false;
    return this.purchaseOrder.status === PurchaseOrderStatus.EM_APROVACAO;
  }

  /** Solicitação de compras: exibe atalho para `/purchase-requests/:id/approve`. */
  canOpenManagerDecision(): boolean {
    if (!this.purchaseOrder || !this.isSolicitacaoComprasFlow) return false;
    const n = String(this.purchaseOrder.status || '').trim().toLowerCase();
    const finalized = [
      PurchaseOrderStatus.APROVADO,
      PurchaseOrderStatus.REJEITADO,
      PurchaseOrderStatus.CANCELADO,
      PurchaseOrderStatus.ENTREGUE_TOTAL
    ].map(s => s.toLowerCase());
    if (finalized.includes(n)) return false;
    return (
      n === PurchaseOrderStatus.SOLICITADO.toLowerCase() ||
      n === PurchaseOrderStatus.EM_APROVACAO.toLowerCase()
    );
  }

  /**
   * Verifica se pode cancelar
   */
  canCancel(): boolean {
    if (!this.purchaseOrder) return false;
    return this.purchaseOrder.status !== PurchaseOrderStatus.CANCELADO && 
           this.purchaseOrder.status !== PurchaseOrderStatus.ENTREGUE_TOTAL;
  }

  /**
   * Habilita o botão «Indicar compra» na tela de detalhes do pedido de compra.
   *
   * Regra de negócio: só aparece quando TODAS as aprovações da faixa estão
   * concluídas (sem etapas pendentes nem etapa atual em "Pendente") e o
   * pedido ainda não foi para o fluxo de compra / entrega / cancelamento.
   *
   * A checagem de status usa palavras-chave (tolera "Comprado", "Em Compra",
   * "Compra realizada", etc.). Backend valida em definitivo; aqui é UX.
   */
  canConfirmPurchase(): boolean {
    if (!this.purchaseOrder || this.isSolicitacaoComprasFlow) return false;
    const status = String(this.purchaseOrder.status || '').toLowerCase().trim();
    if (!status) return false;

    // Estados pós-compra / finalizados — esconde o atalho.
    const postPurchaseOrFinalizedKeys = [
      'em compra',
      'comprad',
      'compra realizada',
      'compra efetuada',
      'entregue',
      'cancelad',
      'rejeit'
    ];
    if (postPurchaseOrFinalizedKeys.some((k) => status.includes(k))) return false;

    // Estados ainda pendentes de aprovação — também esconde.
    const pendingApprovalKeys = ['solicit', 'em aprovação', 'em aprovacao', 'aguardando'];
    if (pendingApprovalKeys.some((k) => status.includes(k))) return false;

    // Só permite indicar compra depois que TODAS as aprovações foram concluídas.
    if (!this.areAllApprovalsCompleted()) return false;

    return true;
  }

  /**
   * Considera todas as aprovações concluídas quando:
   *  - o tracking já foi carregado;
   *  - não há etapas pendentes (`pendingSteps` vazio ou ausente);
   *  - e a etapa atual (se houver) não está em "Pendente".
   * Em caso de tracking ausente, retorna `false` para não liberar o botão
   * sem informação suficiente.
   */
  private areAllApprovalsCompleted(): boolean {
    const t = this.approvalTracking;
    if (!t) return false;
    const pendingCount = (t.pendingSteps || []).length;
    if (pendingCount > 0) return false;
    const cur = t.currentStep;
    if (cur) {
      const action = String(cur.action || '').trim().toLowerCase();
      if (action === 'pendente') return false;
    }
    return true;
  }

  /**
   * Abre a modal de «Indicar compra → estoque». A confirmação dispara em sequência:
   *  1. `POST /stock-movements/entry-awaiting-receipt` (entrada aguardando recebimento);
   *  2. `POST /purchase-orders/{id}/send-to-approval` (registro na faixa de valor / fluxo de compras);
   *  3. `POST /purchase-orders/{id}/confirm-purchase` (confirma a indicação de compra).
   *
   * A modal vem pré-preenchida com os itens do pedido + preço unitário da
   * proposta vencedora (quando houver). O usuário ainda pode editar tudo.
   */
  openStockEntryModal(): void {
    if (!this.canConfirmPurchase() || !this.purchaseOrderId) return;
    if (this.isConfirmingPurchase) return;
    this.stockEntryForm = this.emptyStockEntryForm();
    this.hasWinnerProposal = false;
    this.winnerProposalLines = new Map();
    this.showStockEntryModal = true;
    this.loadWarehousesForEntry();
    this.loadWinnerProposalAndPrefill();
  }

  closeStockEntryModal(): void {
    if (this.isConfirmingPurchase) return;
    this.showStockEntryModal = false;
  }

  /** Recarrega a lista de almoxarifados (botão refresh no select). */
  reloadWarehouses(): void {
    if (this.isLoadingWarehouses || this.isConfirmingPurchase) return;
    this.loadWarehousesForEntry();
  }

  /**
   * Lista os almoxarifados cadastrados no sistema (mesma fonte usada pelas
   * demais telas de Estoque: `GET /warehouse?page=1&pageSize=1000`). Itens
   * ordenados por nome para facilitar a seleção no dropdown.
   *
   * Em runtime, `warehousesLoadError` distingue «falha de rede/permissão» de
   * «cadastro vazio» (lista chega com `items: []`) — assim a UI exibe um
   * texto adequado em cada caso.
   */
  private loadWarehousesForEntry(): void {
    this.isLoadingWarehouses = true;
    this.warehousesLoadError = false;
    this.warehousesService
      .getAll({ page: 1, pageSize: 1000 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          const items = Array.isArray(resp?.items) ? resp!.items : [];
          this.warehouses = items
            .slice()
            .sort((a, b) =>
              (a.name || '').localeCompare(b.name || '', 'pt-BR')
            );
          this.isLoadingWarehouses = false;
          if (this.warehouses.length === 0) {
            console.warn(
              '[PurchaseOrderDetails] /warehouse retornou lista vazia. Resposta crua:',
              resp
            );
          }
        },
        error: (err) => {
          console.error(
            '[PurchaseOrderDetails] Falha ao carregar almoxarifados:',
            err
          );
          this.warehouses = [];
          this.warehousesLoadError = true;
          this.isLoadingWarehouses = false;
        }
      });
  }

  /**
   * Localiza a proposta vencedora deste pedido e usa o preço/qty da linha
   * vencedora para pré-popular o form.
   *
   * Estratégia (resiliente):
   *  1. `listHeaders(poId)` → cabeçalhos dos processos.
   *  2. Para cada header **não cancelado**, chama `listQuotationsForHeader(h.id)`
   *     em paralelo (esse é o endpoint que efetivamente devolve as propostas
   *     com `isWinner` + `lines`; o `listHeaders` retorna só metadados).
   *  3. Procura `isWinner === true` em todas as propostas coletadas.
   *  4. Se o vencedor vier sem `lines` (alguns endpoints listam só agregados),
   *     busca o detalhe via `getQuotationById` como último fallback.
   */
  private loadWinnerProposalAndPrefill(): void {
    if (!this.purchaseOrderId) {
      this.prefillStockEntryForm();
      return;
    }
    this.isLoadingWinnerProposal = true;
    this.quotationsService
      .listHeaders(this.purchaseOrderId, 1, 50)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          const headers = (page?.items || []).filter(
            (h) => !this.isQuotationHeaderCancelled(h)
          );
          if (headers.length === 0) {
            this.applyWinnerProposal(null);
            return;
          }
          forkJoin(
            headers.map((h) =>
              this.quotationsService
                .listQuotationsForHeader(h.id, 1, 50)
                .pipe(
                  map((p) => p?.items || []),
                  catchError(() => of<PurchaseOrderQuotation[]>([]))
                )
            )
          )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (groups) => {
                const allProposals = (groups || []).flat();
                const winner =
                  allProposals.find((p) => !!p?.isWinner) || null;

                if (winner && (!winner.lines || winner.lines.length === 0)) {
                  // Algumas APIs não retornam `lines` no list — busca o detalhe.
                  this.quotationsService
                    .getQuotationById(winner.id)
                    .pipe(
                      catchError(() => of<PurchaseOrderQuotation | null>(winner)),
                      takeUntil(this.destroy$)
                    )
                    .subscribe((detail) =>
                      this.applyWinnerProposal(detail || winner)
                    );
                } else {
                  this.applyWinnerProposal(winner);
                }
              },
              error: () => this.applyWinnerProposal(null)
            });
        },
        error: () => this.applyWinnerProposal(null)
      });
  }

  private applyWinnerProposal(winner: PurchaseOrderQuotation | null): void {
    const linesMap = new Map<number, PurchaseOrderQuotationLine>();
    if (winner) {
      this.hasWinnerProposal = true;
      for (const ln of winner.lines || []) {
        if (ln.purchaseOrderItemId) {
          linesMap.set(ln.purchaseOrderItemId, ln);
        }
      }
    } else {
      this.hasWinnerProposal = false;
    }
    this.winnerProposalLines = linesMap;
    this.isLoadingWinnerProposal = false;
    this.prefillStockEntryForm();
  }

  private isQuotationHeaderCancelled(h: PurchaseOrderQuotationHeader): boolean {
    const s = (h?.status || '').toLowerCase();
    return s === 'cancelled' || s === 'canceled' || s === 'cancelado';
  }

  /**
   * Pré-popula `stockEntryForm.lines` a partir dos itens do pedido e da
   * proposta vencedora (quando disponível).
   *
   * Defaults:
   *  - `name` ← productName / itemName
   *  - `code` ← productCode (vazio se não houver)
   *  - `unitOfMeasure` ← unit (fallback 'UN')
   *  - `estimatedUnitCost` ← quotedUnitPrice do vencedor (fallback: estimatedUnitPrice)
   *  - `quantity` ← quantityApproved (fallback: quantityRequested)
   *  - `note` ← "Pedido <orderNumber>"
   */
  private prefillStockEntryForm(): void {
    const items = this.items || [];
    const orderNumber = this.purchaseOrder?.orderNumber || '';
    const baseNote = orderNumber ? `Pedido ${orderNumber}` : 'Pedido';
    this.stockEntryForm = {
      notes: orderNumber
        ? `Pedido ${orderNumber} — aguardando conferência`
        : 'Pedido — aguardando conferência',
      warehouseId: null,
      lines: items.map((it) => {
        const winLine = this.winnerProposalLines.get(it.id);
        const winnerUnit = this.toFiniteNumber(winLine?.quotedUnitPrice);
        const estimated = this.toFiniteNumber(it.estimatedUnitPrice);
        const unitCost = winnerUnit != null ? winnerUnit : estimated != null ? estimated : 0;
        const qtyApproved = this.toFiniteNumber(it.quantityApproved);
        const qtyRequested = this.toFiniteNumber(it.quantityRequested);
        const quantity =
          qtyApproved != null && qtyApproved > 0
            ? qtyApproved
            : qtyRequested != null && qtyRequested > 0
              ? qtyRequested
              : 0;
        return {
          purchaseOrderItemId: it.id,
          expectedItem: {
            name: (it.productName || it.itemName || '').trim(),
            code: (it.productCode || '').trim(),
            supplierCode: '',
            unitOfMeasure: (it.unit || 'UN').trim(),
            estimatedUnitCost: Math.round((unitCost + Number.EPSILON) * 100) / 100,
            category: ''
          },
          quantity,
          note: baseNote
        };
      })
    };
  }

  private toFiniteNumber(v: unknown): number | null {
    if (v === undefined || v === null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  /**
   * Habilita o botão «Indicar compra» da modal somente quando o form está válido.
   * `code` é opcional (o operador pode não conhecer o código ou o item pode
   * ainda não estar cadastrado).
   */
  canSubmitStockEntry(): boolean {
    if (!this.stockEntryForm.warehouseId) return false;
    if (!this.stockEntryForm.lines.length) return false;
    return this.stockEntryForm.lines.every((l) => {
      const ei = l.expectedItem;
      return (
        !!ei.name?.trim() &&
        !!ei.unitOfMeasure?.trim() &&
        Number(l.quantity) > 0
      );
    });
  }

  /**
   * Submete a modal:
   *  1) cria a entrada no estoque (`entryAwaitingReceipt`);
   *  2) `sendToApproval` com `staffId` do solicitante do pedido (fallback: JWT);
   *  3) `confirmPurchase`.
   *
   * Se o passo (1) falhar, os passos de compras não rodam.
   *
   * Se (2) ou (3) falhar após (1), a entrada pode já existir — a UI avisa para
   * conferência manual do pedido.
   */
  submitStockEntryAndConfirmPurchase(): void {
    if (!this.purchaseOrderId || !this.canSubmitStockEntry()) return;
    if (this.isConfirmingPurchase) return;

    const warehouseId = Number(this.stockEntryForm.warehouseId);
    const payload: EntryAwaitingReceiptPayload = {
      notes: this.stockEntryForm.notes?.trim() || undefined,
      lines: this.stockEntryForm.lines.map((l) => ({
        expectedItem: {
          name: l.expectedItem.name.trim(),
          code: l.expectedItem.code?.trim() || undefined,
          supplierCode: l.expectedItem.supplierCode?.trim() || undefined,
          unitOfMeasure: l.expectedItem.unitOfMeasure.trim(),
          estimatedUnitCost: Number(l.expectedItem.estimatedUnitCost) || 0,
          category: l.expectedItem.category?.trim() || undefined
        },
        quantity: Number(l.quantity),
        warehouseId,
        note: l.note?.trim() || undefined
      }))
    };

    this.isConfirmingPurchase = true;
    this.stockMovementsService
      .entryAwaitingReceipt(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.runConfirmPurchaseAfterStock(),
        error: (err: unknown) => {
          this.isConfirmingPurchase = false;
          this.errorService.handleHttpError(err);
        }
      });
  }

  private runConfirmPurchaseAfterStock(): void {
    if (!this.purchaseOrderId) {
      this.isConfirmingPurchase = false;
      return;
    }
    const staffIdForSendToApproval = this.resolveStaffIdForSendToApproval();
    if (staffIdForSendToApproval == null || staffIdForSendToApproval <= 0) {
      this.isConfirmingPurchase = false;
      this.errorService.showErrorMessage(
        'A entrada de estoque foi criada, mas não foi possível identificar o solicitante do pedido (staffId) para enviar à aprovação. Verifique o pedido manualmente.'
      );
      return;
    }

    this.purchaseOrderService
      .sendToApproval(this.purchaseOrderId, {
        staffId: staffIdForSendToApproval,
        createdBy: this.resolveCreatedByQueryForSendToApproval()
      })
      .pipe(
        concatMap(() => this.purchaseOrderService.confirmPurchase(this.purchaseOrderId!)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.isConfirmingPurchase = false;
          this.showStockEntryModal = false;
          this.errorService.showSuccessMessage(
            'Compra indicada e entrada de estoque criada com sucesso.'
          );
          this.retryLoad();
        },
        error: (err: unknown) => {
          this.isConfirmingPurchase = false;
          this.errorService.showErrorMessage(
            'A entrada de estoque foi criada, mas houve falha ao concluir o pedido em compras (envio para aprovação ou confirmação). Verifique o pedido manualmente.'
          );
          this.errorService.handleHttpError(err);
        }
      });
  }

  /**
   * Body de `send-to-approval`: prioriza o solicitante do pedido (`requestedByStaffId`),
   * como esperado pelo microserviço de compras; se ausente, usa o staff do JWT.
   */
  private resolveStaffIdForSendToApproval(): number | null {
    const req = this.purchaseOrder?.requestedByStaffId;
    if (req != null && Number.isFinite(Number(req)) && Number(req) > 0) {
      return Number(req);
    }
    return this.authService.getStaffIdFromToken();
  }

  /** Query `createdBy` exigida pelo shopping em `send-to-approval` (PUT). */
  private resolveCreatedByQueryForSendToApproval(): string | undefined {
    const po = this.purchaseOrder;
    if (!po) {
      return undefined;
    }
    const a = (po.createdBy || '').trim();
    if (a) {
      return a;
    }
    const b = (po.requestedByStaffName || '').trim();
    return b || undefined;
  }

  private emptyStockEntryForm(): StockEntryFormModel {
    return { notes: '', warehouseId: null, lines: [] };
  }

  /**
   * Aprova pedido rapidamente
   */
  quickApprove(): void {
    if (!this.purchaseOrder) return;

    const staffId = 1; // TODO: Obter do usuário logado
    
    this.purchaseOrderService.approvePurchaseOrder(this.purchaseOrder.id, { 
      staffId,
      comments: 'Aprovação rápida via detalhes do pedido'
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.errorService.showInfoMessage('Pedido aprovado com sucesso');
          this.retryLoad();
        },
        error: (error) => {
          this.errorService.handleHttpError(error);
        }
      });
  }

  /**
   * Rejeita pedido rapidamente
   */
  quickReject(): void {
    if (!this.purchaseOrder) return;

    const reason = prompt('Motivo da rejeição:');
    if (!reason) return;

    const staffId = 1; // TODO: Obter do usuário logado
    
    this.purchaseOrderService.rejectPurchaseOrder(this.purchaseOrder.id, { 
      staffId, 
      rejectionReason: reason 
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.errorService.showInfoMessage('Pedido rejeitado com sucesso');
          this.retryLoad();
        },
        error: (error) => {
          this.errorService.handleHttpError(error);
        }
      });
  }

  // ===== MÉTODOS DE FORMATAÇÃO E DISPLAY =====

  /**
   * Obtém classe CSS para status
   */
  getStatusClass(status: PurchaseOrderStatus | string): string {
    switch (status) {
      case PurchaseOrderStatus.SOLICITADO:
        return 'bg-info';
      case PurchaseOrderStatus.EM_APROVACAO:
        return 'bg-warning';
      case PurchaseOrderStatus.APROVADO:
        return 'bg-success';
      case PurchaseOrderStatus.REJEITADO:
        return 'bg-danger';
      case PurchaseOrderStatus.EM_COMPRA:
        return 'bg-primary';
      case PurchaseOrderStatus.ENTREGUE_PARCIAL:
        return 'bg-info';
      case PurchaseOrderStatus.ENTREGUE_TOTAL:
        return 'bg-success';
      case PurchaseOrderStatus.CANCELADO:
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Obtém nome de exibição do status
   */
  getStatusDisplayName(status: PurchaseOrderStatus | string): string {
    switch (status) {
      case PurchaseOrderStatus.SOLICITADO:
        return 'Solicitado';
      case PurchaseOrderStatus.EM_APROVACAO:
        return 'Em Aprovação';
      case PurchaseOrderStatus.APROVADO:
        return 'Aprovado';
      case PurchaseOrderStatus.REJEITADO:
        return 'Rejeitado';
      case PurchaseOrderStatus.EM_COMPRA:
        return 'Em Compra';
      case PurchaseOrderStatus.ENTREGUE_PARCIAL:
        return 'Entregue Parcialmente';
      case PurchaseOrderStatus.ENTREGUE_TOTAL:
        return 'Entregue Totalmente';
      case PurchaseOrderStatus.CANCELADO:
        return 'Cancelado';
      default:
        return status;
    }
  }

  /**
   * Obtém classe CSS para prioridade
   */
  getPriorityClass(priority: PurchaseOrderPriority | string): string {
    switch (priority) {
      case PurchaseOrderPriority.BAIXA:
        return 'bg-secondary';
      case PurchaseOrderPriority.NORMAL:
        return 'bg-info';
      case PurchaseOrderPriority.ALTA:
        return 'bg-warning';
      case PurchaseOrderPriority.URGENTE:
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Obtém nome de exibição da prioridade
   */
  getPriorityDisplayName(priority: PurchaseOrderPriority | string): string {
    switch (priority) {
      case PurchaseOrderPriority.BAIXA:
        return 'Baixa';
      case PurchaseOrderPriority.NORMAL:
        return 'Normal';
      case PurchaseOrderPriority.ALTA:
        return 'Alta';
      case PurchaseOrderPriority.URGENTE:
        return 'Urgente';
      default:
        return priority;
    }
  }

  /**
   * Obtém ícone para aprovação
   */
  getApprovalIcon(status: ApprovalStatus | string): string {
    switch (status) {
      case ApprovalStatus.APROVADO:
        return 'fa-check';
      case ApprovalStatus.REJEITADO:
        return 'fa-times';
      case ApprovalStatus.PENDENTE:
        return 'fa-clock';
      default:
        return 'fa-question';
    }
  }

  /**
   * Obtém classe CSS para status de aprovação
   */
  getApprovalStatusClass(status: ApprovalStatus | string): string {
    switch (status) {
      case ApprovalStatus.APROVADO:
        return 'bg-success';
      case ApprovalStatus.REJEITADO:
        return 'bg-danger';
      case ApprovalStatus.PENDENTE:
        return 'bg-warning';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Obtém nome de exibição do status de aprovação
   */
  getApprovalStatusDisplayName(status: ApprovalStatus | string): string {
    switch (status) {
      case ApprovalStatus.APROVADO:
        return 'Aprovado';
      case ApprovalStatus.REJEITADO:
        return 'Rejeitado';
      case ApprovalStatus.PENDENTE:
        return 'Pendente';
      default:
        return status;
    }
  }

  // ----- Histórico de aprovações (visão geral): campos extras / PascalCase -----

  formatDateTime(value: Date | string | undefined | null): string {
    if (value === undefined || value === null || value === '') return '—';
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  }

  /** Lê primeiro valor não vazio (camel ou Pascal, como vem da API). */
  approvalRawPick(a: PurchaseOrderApproval, keys: string[]): string {
    const r = a as unknown as Record<string, unknown>;
    for (const k of keys) {
      const v = r[k];
      if (v === null || v === undefined) continue;
      const s = String(v).trim();
      if (s) return s;
    }
    return '';
  }

  approvalStaffLabel(a: PurchaseOrderApproval): string {
    const name = this.approvalRawPick(a, [
      'approverStaffName',
      'ApproverStaffName',
      'staffName',
      'StaffName'
    ]);
    if (name) return name;
    const sid = Number(
      (a as unknown as Record<string, unknown>)['staffId'] ??
        (a as unknown as Record<string, unknown>)['StaffId'] ??
        a.staffId
    );
    return Number.isFinite(sid) && sid > 0 ? `Colaborador #${sid}` : '—';
  }

  approvalActionLabel(a: PurchaseOrderApproval): string {
    return this.approvalRawPick(a, [
      'actionDisplayName',
      'ActionDisplayName',
      'action',
      'Action'
    ]);
  }

  approvalLevelLabel(a: PurchaseOrderApproval): string {
    return this.approvalRawPick(a, [
      'approvalLevelDisplayName',
      'ApprovalLevelDisplayName',
      'approvalLevel',
      'ApprovalLevel'
    ]);
  }

  approvalStatusLabel(a: PurchaseOrderApproval): string {
    return this.approvalRawPick(a, ['status', 'Status']);
  }

  approvalCommentsText(a: PurchaseOrderApproval): string {
    return this.approvalRawPick(a, ['comments', 'Comments']);
  }

  approvalRejectionText(a: PurchaseOrderApproval): string {
    return this.approvalRawPick(a, ['rejectionReason', 'RejectionReason']);
  }

  approvalRequestedChangesText(a: PurchaseOrderApproval): string {
    return this.approvalRawPick(a, ['requestedChanges', 'RequestedChanges']);
  }

  approvalRecordIdLabel(a: PurchaseOrderApproval): string {
    const r = a as unknown as Record<string, unknown>;
    const id = r['id'] ?? r['Id'] ?? a.id;
    return id != null && String(id).trim() !== '' ? String(id) : '—';
  }

  approvalOrderLabel(a: PurchaseOrderApproval): string {
    const r = a as unknown as Record<string, unknown>;
    const n = Number(r['approvalOrder'] ?? r['ApprovalOrder'] ?? a.approvalOrder ?? NaN);
    return Number.isFinite(n) ? String(n) : '—';
  }

  approvalHeadline(a: PurchaseOrderApproval): string {
    const act = this.approvalActionLabel(a);
    const st = this.approvalStatusLabel(a);
    if (act && st && act.toLowerCase() !== st.toLowerCase()) {
      return `${act} · Situação: ${st}`;
    }
    return act || st || 'Registro de aprovação';
  }

  approvalUiBadgeClass(a: PurchaseOrderApproval): string {
    const sc = this.approvalRawPick(a, ['statusColor', 'StatusColor']).toLowerCase();
    const byColor: Record<string, string> = {
      success: 'bg-success',
      danger: 'bg-danger',
      warning: 'bg-warning text-dark',
      info: 'bg-info text-dark',
      secondary: 'bg-secondary'
    };
    if (sc && byColor[sc]) return byColor[sc];

    const blob = `${this.approvalStatusLabel(a)} ${this.approvalActionLabel(a)}`.toLowerCase();
    if (blob.includes('rejeit')) return 'bg-danger';
    if (blob.includes('cancel')) return 'bg-secondary';
    if (blob.includes('pend')) return 'bg-warning text-dark';
    if (blob.includes('aprov')) return 'bg-success';
    if (blob.includes('alteraç') || blob.includes('solicit')) return 'bg-info text-dark';
    return 'bg-secondary';
  }

  approvalUiIconClass(a: PurchaseOrderApproval): string {
    const ic = this.approvalRawPick(a, ['statusIcon', 'StatusIcon']).toLowerCase().replace(/_/g, '-');
    const map: Record<string, string> = {
      'check-circle': 'fa-check-circle',
      check: 'fa-check',
      clock: 'fa-clock',
      'x-circle': 'fa-times-circle',
      times: 'fa-times',
      edit: 'fa-edit'
    };
    if (ic && map[ic]) return `fas ${map[ic]}`;

    const st = `${this.approvalStatusLabel(a)} ${this.approvalActionLabel(a)}`.toLowerCase();
    if (st.includes('rejeit') || st.includes('cancel')) return 'fas fa-times-circle';
    if (st.includes('pend')) return 'fas fa-clock';
    if (st.includes('aprov')) return 'fas fa-check-circle';
    return 'fas fa-clipboard-check';
  }

  approvalFlagFinal(a: PurchaseOrderApproval): boolean {
    const r = a as unknown as Record<string, unknown>;
    return Boolean(r['isFinalApproval'] ?? r['IsFinalApproval'] ?? a.isFinalApproval);
  }

  approvalFlagAutomatic(a: PurchaseOrderApproval): boolean {
    const r = a as unknown as Record<string, unknown>;
    return Boolean(r['isAutomaticApproval'] ?? r['IsAutomaticApproval'] ?? a.isAutomaticApproval);
  }

  approvalFlagManager(a: PurchaseOrderApproval): boolean {
    const r = a as unknown as Record<string, unknown>;
    return Boolean(r['isManagerApproval'] ?? r['IsManagerApproval']);
  }

  // ===== FORMATAÇÃO =====

  /**
   * Formata valor monetário
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Formata data
   */
  formatDate(date: Date | string | undefined): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-BR');
  }

  /** Exibe Sim/Não para flags de aprovação e permissões no cabeçalho do pedido. */
  yn(value: boolean | undefined | null): string {
    if (value === true) return 'Sim';
    if (value === false) return 'Não';
    return '—';
  }

  private static fmtQty(v: unknown): string {
    if (v === null || v === undefined || v === '') return '—';
    const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
  }

  private static fmtBrl(v: unknown): string {
    if (v === null || v === undefined || v === '') return '—';
    const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
    if (!Number.isFinite(n)) return '—';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
  }

  /**
   * Preenche `displayLineTotal` para a tabela: prioriza total real quando maior que zero, senão estimado.
   */
  private mapItemRowForDisplay(item: PurchaseOrderItem): PurchaseOrderItem & { displayLineTotal: number } {
    const est = item.estimatedTotalPrice;
    const act = item.actualTotalPrice;
    const nEst = est != null ? Number(est) : NaN;
    const nAct = act != null ? Number(act) : NaN;
    let displayLineTotal = 0;
    if (Number.isFinite(nAct) && nAct > 0) {
      displayLineTotal = nAct;
    } else if (Number.isFinite(nEst) && nEst > 0) {
      displayLineTotal = nEst;
    } else if (Number.isFinite(nAct)) {
      displayLineTotal = nAct;
    } else if (Number.isFinite(nEst)) {
      displayLineTotal = nEst;
    }
    return { ...item, displayLineTotal };
  }

  private mapSimpleViewToPurchaseOrder(simpleView: PurchaseRequestSimpleViewResponse): PurchaseOrder {
    return {
      id: simpleView.id,
      orderNumber: `#${simpleView.id}`,
      requestDate: new Date(simpleView.requestDate),
      status: simpleView.status || 'Solicitado',
      priority: 'Normal',
      subTotal: 0,
      totalAmount: 0,
      requestedByStaffId: simpleView.requestedByStaffId || 0,
      requestedByStaffName: simpleView.requestedByName || 'Não informado',
      departmentName: simpleView.requestedByDepartmentName,
      approvedByStaffId: simpleView.managerApproverStaffId,
      approvedByStaffName: simpleView.managerApproverName,
      costCenterId: simpleView.costCenterId,
      costCenterName: simpleView.costCenterName,
      requiresApproval: true,
      description: simpleView.description || '',
      notes: simpleView.notes || '',
      rejectionReason: simpleView.approvalSituation,
      items: [],
      approvals: [],
      daysToDelivery: 0,
      isOverdue: false,
      isPartiallyReceived: false,
      canBeApproved: false,
      canBeRejected: false,
      canBeCancelled: false,
      totalItems: simpleView.items?.length || 0,
      approvedItems: 0,
      receivedItems: 0,
      receiptPercentage: 0,
      daysInCurrentStatus: 0,
      isActive: true
    };
  }
}
